<template>
  <Teleport to="body">
    <div v-if="isVisible" class="modal-overlay" @keydown.esc="closeModal">
      <ModalLoading :isLoading="isLoading" />
      <div class="modal-content">
        <button class="modal-close-x" @click="closeModal">✕</button>
        <div class="header-container" tabindex="-1">
          <h2 class="modal-title">Clientes</h2>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Búsqueda por nombre"
            class="search-input"
          />
        </div>
        
        <div class="form-section" v-if="showForm">
          <div class="form-container">
            <div class="form-group">
              <label>Nombre Completo: <span class="required">*</span></label>
              <input 
                type="text" 
                v-model="newClient.nombre_completo"
                :class="{ 'error': !newClient.nombre_completo.trim() && showValidation }"
              />
            </div>
            
            <div class="form-group">
              <label>Correo:</label>
              <input type="email" v-model="newClient.correo" />
            </div>
            
            <div class="form-group">
              <label>RTN: <span class="required">*</span></label>
              <input 
                type="text" 
                v-model="newClient.rtn"
                :class="{ 'error': !newClient.rtn.trim() && showValidation }"
                @input="formatearRTN"
                placeholder="0000000000000"
                maxlength="17"
              />
            </div>

            <!-- NUEVOS CAMPOS PARA TERCERA EDAD -->
            <div class="form-group">
              <label>Fecha de Nacimiento: <span class="required">*</span></label>
              <input 
                type="date" 
                v-model="newClient.fecha_nacimiento"
                :max="fechaMaxima"
                :min="fechaMinima"
                :class="{ 'error': !newClient.fecha_nacimiento && showValidation }"
                @change="calcularEdad"
              />
            </div>

            <div class="form-group">
              <label>Número de Identidad: <span class="required">*</span></label>
              <input 
                type="text" 
                v-model="newClient.numero_identidad"
                placeholder="0801-1990-12345"
                maxlength="15"
                :class="{ 'error': !newClient.numero_identidad.trim() && showValidation }"
                @input="formatearIdentidad"
              />
            </div>

            <div class="form-group">
              <label>Teléfono:</label>
              <div class="phone-input-container">
                <select
                  v-model="selectedCountry"
                  @change="updatePhoneValidation"
                  class="select-phone"
                >
                  <option value="">País</option>
                  <option
                    v-for="(country, code) in countryData"
                    :key="code"
                    :value="code"
                  >
                    {{ country.emoji }} {{ country.code }}
                  </option>
                </select>
                <input
                  v-model="newClient.telefono"
                  type="text"
                  class="input-phone"
                  :placeholder="'Número (' + phoneLength + ' dígitos)'"
                />
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label>Dirección:</label>
            <input type="text" v-model="newClient.direccion" />
          </div>

          <!-- INDICADOR DE TERCERA EDAD -->
          <div v-if="esTerceraEdad" class="tercera-edad-indicator">
            <span> Cliente elegible para descuento de tercera edad (20%) - Edad: {{ edadCalculada }} años</span>
          </div>

          <!-- Mostrar errores de validación -->
          <div v-if="showValidation && !formularioValido" class="validation-errors">
            <p>⚠️ Por favor complete todos los campos obligatorios marcados con <span class="required">*</span></p>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th class="col-nombre">Nombre</th>
                <th class="col-direccion">Dirección</th>
                <th class="col-rtn">RTN</th>
                <th class="col-identidad">Identidad</th>
                <th class="col-edad">Edad</th>
                <th class="col-telefono">Teléfono</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="client in filterClients"
                :key="client.rtn"
                @dblclick="selectClient(client)"
                class="client-row"
                :class="{ 'tercera-edad-row': client.es_tercera_edad }"
              >
                <td class="col-nombre">
                  {{ client.nombre_completo }}
                  <span v-if="client.es_tercera_edad" class="tercera-edad-badge"></span>
                </td>
                <td class="col-direccion">{{ client.direccion }}</td>
                <td class="col-rtn">{{ client.rtn }}</td>
                <td class="col-identidad">{{ client.numero_identidad || '-' }}</td>
                <td class="col-edad">{{ calcularEdadCliente(client.fecha_nacimiento) }}</td>
                <td class="col-telefono">{{ client.telefono }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="button-container">
          <div>
            <button
              v-if="showForm"
              @click="addClient"
              style="margin-right: 15px"
              :disabled="!formularioValido"
              :class="{ 'btn-disabled': !formularioValido }"
            >
              Guardar
            </button>
            <button v-if="puedeCrearCliente || showForm" @click="toggleForm">
              {{ showForm ? "Cancelar" : "Agregar nuevo cliente" }}
            </button>
          </div>
          <button @click="closeModal">Salir</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
const { postCliente } = require("../../../services/clienteSolicitudes.js");
import { notificaciones } from "../../../services/notificaciones.js";
import { validacionesClientes } from "../../../services/validarCampos.js";
import { COUNTRY_CODES } from "../../../services/countrySelector.js";
import { validarTerceraEdad } from "../../../services/ventasSolicitudes.js";

export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    id_usuario: {
      type: Number,
      required: true,
    },
    clientes: {
      type: Array,
      required: true,
    },
    puedeCrearCliente: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close', 'client-selected', 'modal-focused'],
  data() {
    return {
      searchQuery: "",
      selectedCountry: "HN",
      countryData: COUNTRY_CODES,
      phoneLength: 8,
      isLoading: false,
      showForm: false,
      showValidation: false,
      filteredClients: [],
      edadCalculada: 0,
      newClient: {
        id_cliente: "",
        nombre_completo: "",
        correo: "",
        direccion: "",
        rtn: "",
        telefono: "",
        fecha_nacimiento: "",
        numero_identidad: "",
        es_tercera_edad: false,
      },
      clients: [...this.clientes],
    };
  },
  computed: {
    filterClients() {
      if (this.searchQuery === "") {
        return this.clients;
      } else {
        return this.clients.filter((client) =>
          client.nombre_completo
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        );
      }
    },
    
    fechaMaxima() {
      // Fecha máxima es hoy
      return new Date().toISOString().split('T')[0];
    },

    fechaMinima() {
      // Fecha mínima para personas de máximo 120 años
      const fecha = new Date();
      fecha.setFullYear(fecha.getFullYear() - 120);
      return fecha.toISOString().split('T')[0];
    },
    
    esTerceraEdad() {
      return this.newClient.fecha_nacimiento && this.edadCalculada >= 60;
    },
    
    formularioValido() {
      return this.newClient.nombre_completo.trim() !== '' &&
             this.newClient.rtn.trim() !== '' &&
             this.newClient.fecha_nacimiento !== '' &&
             this.newClient.numero_identidad.trim() !== '' &&
             this.edadCalculada >= 18 && this.edadCalculada <= 120;
    }
  },
  
  watch: {
    clientes(newValue) {
      if (newValue) {
        this.clients = newValue;
      }
    },
    
    esTerceraEdad(newVal) {
      this.newClient.es_tercera_edad = newVal;
    }
  },
  
  methods: {
    abrirFormularioDirectamente() {
      this.showForm = true;
      this.resetNewClient();
      this.selectedCountry = "HN";
      this.updatePhoneValidation();
    },

    resetNewClient() {
      this.newClient = {
        id_cliente: "",
        nombre_completo: "",
        correo: "",
        direccion: "",
        rtn: "",
        telefono: "",
        fecha_nacimiento: "",
        numero_identidad: "",
        es_tercera_edad: false,
      };
      this.edadCalculada = 0;
      this.showValidation = false;
    },

    calcularEdad() {
      if (!this.newClient.fecha_nacimiento) {
        this.edadCalculada = 0;
        return;
      }
      
      const fechaNacimiento = new Date(this.newClient.fecha_nacimiento);
      const hoy = new Date();
      
      // Validar que la fecha no sea futura
      if (fechaNacimiento > hoy) {
        notificaciones("error", "La fecha de nacimiento no puede ser en el futuro");
        this.newClient.fecha_nacimiento = "";
        this.edadCalculada = 0;
        return;
      }
      
      let edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
      const mesCumplido = hoy.getMonth() > fechaNacimiento.getMonth() ||
        (hoy.getMonth() === fechaNacimiento.getMonth() && hoy.getDate() >= fechaNacimiento.getDate());
      
      if (!mesCumplido) {
        edad--;
      }
      
      this.edadCalculada = Math.max(0, edad);
      
      // Validar edad razonable
      if (this.edadCalculada < 18) {
        notificaciones("warning", "El cliente debe ser mayor de edad (18 años)");
      } else if (this.edadCalculada > 120) {
        notificaciones("warning", "Por favor verifique la fecha de nacimiento");
      } else if (this.esTerceraEdad) {
        notificaciones("success", `Cliente elegible para descuento de tercera edad (${this.edadCalculada} años)`);
      }
    },

    calcularEdadCliente(fechaNacimiento) {
      if (!fechaNacimiento) return '-';
      
      const fechaNac = new Date(fechaNacimiento);
      const hoy = new Date();
      let edad = hoy.getFullYear() - fechaNac.getFullYear();
      const mesCumplido = hoy.getMonth() > fechaNac.getMonth() ||
        (hoy.getMonth() === fechaNac.getMonth() && hoy.getDate() >= fechaNac.getDate());
      
      if (!mesCumplido) {
        edad--;
      }
      
      return Math.max(0, edad);
    },

    formatearRTN() {
      // Remover todo lo que no sean números
      let valor = this.newClient.rtn.replace(/\D/g, '');
      
      // Limitar a 14 dígitos
      if (valor.length > 14) {
        valor = valor.substring(0, 14);
      }
      
      this.newClient.rtn = valor;
    },

    formatearIdentidad() {
      // Remover todo lo que no sean números
      let valor = this.newClient.numero_identidad.replace(/\D/g, '');
      
      // Limitar a 13 dígitos
      if (valor.length > 13) {
        valor = valor.substring(0, 13);
      }
      
      // Formatear como 0000-0000-00000
      if (valor.length >= 4) {
        valor = valor.substring(0, 4) + '-' + valor.substring(4);
      }
      if (valor.length >= 9) {
        valor = valor.substring(0, 9) + '-' + valor.substring(9);
      }
      
      this.newClient.numero_identidad = valor;
    },

    updatePhoneValidation() {
      if (this.selectedCountry && this.countryData[this.selectedCountry]) {
        this.phoneLength = this.countryData[this.selectedCountry].length;
      }
    },

    toggleForm() {
      this.showForm = !this.showForm;
      if (!this.showForm) {
        this.resetNewClient();
      }
    },
    
    onFocus() {
      this.$emit("modal-focused", true);
    },

    closeModal() {
      this.showForm = false;
      this.$emit("close");
    },

    selectClient(client) {
      this.$emit("client-selected", client);
    },

    async validarClienteBackend() {
      try {
        const resultado = await validarTerceraEdad(
          this.newClient.fecha_nacimiento,
          this.newClient.numero_identidad,
          this.id_usuario
        );
        
        this.newClient.es_tercera_edad = resultado.es_tercera_edad;
        
        if (resultado.es_tercera_edad) {
          notificaciones("success", resultado.mensaje);
        }
        
        return true;
      } catch (error) {
        console.error("Error al validar tercera edad:", error);
        notificaciones("error", "Error al validar los datos del cliente");
        return false;
      }
    },

    async addClient() {
      this.showValidation = true;
      
      if (!this.formularioValido) {
        notificaciones("error", "Por favor complete todos los campos obligatorios");
        return;
      }

      // Validar RTN único
      const rtnExistente = this.clients.find(c => c.rtn === this.newClient.rtn);
      if (rtnExistente) {
        notificaciones("error", "Ya existe un cliente con este RTN");
        return;
      }

      // Validar identidad única
      const identidadExistente = this.clients.find(c => c.numero_identidad === this.newClient.numero_identidad);
      if (identidadExistente) {
        notificaciones("error", "Ya existe un cliente con este número de identidad");
        return;
      }
      
      if (!validacionesClientes.validarCampos(this.newClient, this.selectedCountry)) {
        return;
      }
      
      this.isLoading = true;

      try {
        // Validar en el backend antes de guardar
        const validacionOK = await this.validarClienteBackend();
        if (!validacionOK) {
          return;
        }

        const response = await postCliente(this.newClient, this.id_usuario);
        this.newClient.id_cliente = response[0].id_cliente;

        // Agregar el cliente a la lista local
        this.clients.unshift({ ...this.newClient });
        
        // Limpiar formulario
        this.resetNewClient();
        this.showForm = false;
        
        notificaciones("success", "Cliente agregado exitosamente");
        
      } catch (error) {
        console.error(error);
        notificaciones("error", "Error al agregar el cliente");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
  font-family: "Montserrat", sans-serif;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
  width: 90%;
  max-width: 1400px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.table-container {
  flex: 1;
  overflow: auto;
  position: relative;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

thead {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
}

thead th {
  border-top: 1px solid #ddd;
  border-bottom: 2px solid #ddd;
  background-color: #f8f9fa;
}

th,
td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

/* Proporciones de columnas */
.col-nombre {
  width: 25%;
}

.col-direccion {
  width: 25%;
}

.col-rtn {
  width: 12%;
}

.col-identidad {
  width: 15%;
}

.col-edad {
  width: 8%;
}

.col-telefono {
  width: 15%;
}

.modal-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
}

.form-section {
  margin: 20px 0;
}

.form-container {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 200px;
}

.phone-input-container {
  display: flex;
  gap: 8px;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 40px;
}

/* Indicadores y validaciones */
.required {
  color: #ff4444;
  font-weight: bold;
}

.validation-errors {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 10px;
  margin: 10px 0;
  color: #856404;
}

.error {
  border-color: #ff4444 !important;
  box-shadow: 0 0 0 2px rgba(255, 68, 68, 0.2) !important;
}

.tercera-edad-indicator {
  background-color: #e8f5e8;
  border: 2px solid #4caf50;
  border-radius: 4px;
  padding: 10px;
  margin: 10px 0;
  text-align: center;
  font-weight: bold;
  color: #2e7d32;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.tercera-edad-badge {
  margin-left: 5px;
  font-size: 16px;
}

.tercera-edad-row {
  background-color: #f8fff8 !important;
}

.tercera-edad-row:hover {
  background-color: #e8f5e8 !important;
}

/* Inputs y botones */
input:focus {
  outline: none;
  border-color: #c09d62;
  box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
  transition: all 0.3s ease;
}

.search-input {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 40% !important;
}

input[type="text"],
input[type="email"],
input[type="date"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 80% !important;
}

.button-container {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

button {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #4caf50;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

button:hover:not(:disabled) {
  background-color: #3e8e41;
}

button:disabled,
.btn-disabled {
  background-color: #cccccc !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.client-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.client-row:hover {
  background-color: #f5f5f5;
}

/* Modo oscuro */
.dark .modal-content {
  background: #1a1a1a;
  color: #e5e5e5;
}

.dark .tercera-edad-indicator {
  background-color: #1a2f1a;
  border-color: #00ff95;
  color: #00ff95;
}

.dark .validation-errors {
  background-color: #3d3d00;
  border-color: #ffaa00;
  color: #ffcc66;
}

.dark .tercera-edad-row {
  background-color: #1a2f1a !important;
}

.dark .tercera-edad-row:hover {
  background-color: #2a3f2a !important;
}

.dark thead th {
  background-color: #2b2b2b;
  border-color: #404040;
}

.dark th,
.dark td {
  border-color: #404040;
}

.dark input,
.dark select {
  background-color: #141414;
  border-color: #2b2b2b;
  color: #e5e5e5;
}

.dark .client-row:hover {
  background-color: #2a2a2a;
}

/* Responsividad */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    height: 95vh;
  }

  .form-container {
    flex-direction: column;
  }

  .form-group {
    min-width: 100%;
  }

  .header-container {
    flex-direction: column;
    gap: 10px;
  }

  .search-input {
    width: 100% !important;
  }
}
</style>

<style>
/* Dark mode — no scoped, para alcanzar el modal teleportado al body */
html.dark .modal-content {
  background: #1e1e1e !important;
  color: #e5e7eb !important;
}

html.dark .modal-title { color: #c09d62; }

html.dark .modal-content thead th {
  background-color: #252525;
  color: #c09d62;
  border-color: #3a3a3a;
}
html.dark .modal-content th,
html.dark .modal-content td {
  border-color: #2d2d2d;
  color: #d1d5db;
}
html.dark .modal-content .client-row:hover { background-color: #2a2a2a; }
html.dark .modal-content .tercera-edad-row { background-color: #1a2f1a !important; }
html.dark .modal-content .tercera-edad-row:hover { background-color: #2a3f2a !important; }
html.dark .modal-content .tercera-edad-indicator {
  background-color: #1a2f1a;
  border-color: #4ade80;
  color: #4ade80;
}
html.dark .modal-content input,
html.dark .modal-content select {
  background-color: #2a2a2a;
  border-color: #444;
  color: #e5e7eb;
}
html.dark .modal-content input:focus { border-color: #c09d62; }
html.dark .modal-content .validation-errors {
  background-color: #3d3000;
  border-color: #f59e0b;
  color: #fcd34d;
}
html.dark .modal-content .button-container button {
  background-color: #198754;
  color: #fff;
}
html.dark .modal-content .button-container button:hover:not(:disabled) { background-color: #146c43; }
html.dark .modal-content .modal-close-x {
  background: transparent;
  color: #9ca3af;
  border: none;
  cursor: pointer;
}
html.dark .modal-content .modal-close-x:hover { color: #e5e7eb; }
</style>